/******************************************************************************
 *** BgtCmptnAttachMng ** 결재완료 결의서 첨부관리(통합 리스트)
*******************************************************************************/
import React from "react";
import { useRecoilValue } from "recoil";
import * as Atom from "@/js/common/Atom";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import StyledTabs from "@/js/common/ui/StyledTabs";
import { Tab, TabList } from "react-tabs";
import SelectBox from "@/js/common/ui/input/SelectBox";
import TextBox from "@/js/common/ui/input/TextBox";
import Button from "@/js/common/ui/Button";
import GridTable, { fldInfsProps } from "@/js/common/ui/GridTable";
import AdminGridStyle from "@/js/common/ui/style/AdminGridStyle";
import Modal from "@/js/common/ui/modal/Modal";
import DialogTitleBar from "@/js/system/common/DialogTitleBar";
import BgtCmptnAttachDetail from "./dialog/BgtCmptnAttachDetail";

type MainTab = "OVRL" | "MBSDS" | "SPC";
type SubTab = "DPST" | "EXPND";

interface RowData {
    date: string;
    abstrCn: string;
    amt: number | string;
    rsdocNo: string;
    docId?: string;
    rawData?: any;
}

const BgtCmptnAttachMng = () => {
    const userInfo = useRecoilValue(Atom.userInfo);

    // 1차 탭
    const [mainTab, setMainTab] = React.useState<MainTab>("OVRL");
    const [mainTabIndex, setMainTabIndex] = React.useState<number>(0);

    // 2차 탭
    const [subTab, setSubTab] = React.useState<SubTab>("DPST");
    const [subTabIndex, setSubTabIndex] = React.useState<number>(0);

    // 검색조건
    const [upOgnzCd, setUpOgnzCd] = React.useState<string>(userInfo.upOgnzCd);
    const [ognzCd, setOgnzCd] = React.useState<string>(userInfo.ognzCd);
    const [keyword, setKeyword] = React.useState<string>("");
    const [acntgYear, setAcntgYear] = React.useState<string>(Util.getNowAcntgYear());

    // 모달 상태
    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const [selectedDocId, setSelectedDocId] = React.useState<string>("");

    const [dataArr, setDataArr] = React.useState<RowData[]>([]);
    const [sumAmt, setSumAmt] = React.useState<string>("0");

    const fldInfs: fldInfsProps[] = [
        { type: "date", fldNm: "date", width: 140, headAlign: "center", dataAlign: "center", label: "일자" },
        { type: "text", fldNm: "abstrCn", width: 420, headAlign: "center", dataAlign: "left", label: "적요" },
        { type: "comma", fldNm: "amt", width: 160, headAlign: "center", dataAlign: "right", label: "금액" },
        { type: "text", fldNm: "rsdocNo", width: 200, headAlign: "center", dataAlign: "center", label: "문서번호" },
    ];

    const getTitle = () => {
        if (mainTab === "OVRL") return "총괄교부금";
        if (mainTab === "MBSDS") return "운영비";
        return "특별회계기금";
    };

    const getSubTitle = () => {
        return subTab === "DPST" ? "수입" : "지출";
    };

    // 조회 API URL
    const getApiUrl = () => {
        if (mainTab === "OVRL" && subTab === "DPST") return "system/bgt/ovrl/cmptn/select-ovrl-cmptn-dpst-list.do";
        if (mainTab === "OVRL" && subTab === "EXPND") return "system/bgt/ovrl/cmptn/select-ovrl-cmptn-expnd-list.do";
        if (mainTab === "MBSDS" && subTab === "DPST") return "system/bgt/mbsds/cmptn/select-cmptn-dpst-list.do";
        if (mainTab === "MBSDS" && subTab === "EXPND") return "system/bgt/mbsds/cmptn/select-cmptn-expnd-list.do";

        if (mainTab === "SPC" && subTab === "DPST") return "system/bgt/spc/cmptn/select-bgt-spc-cmptn-all-dpst-list.do";
        return "system/bgt/spc/cmptn/select-bgt-spc-cmptn-all-expnd-list.do";
    };

    // API 파라미터
    const getParamData = () => {
        return {
            orgCd: ognzCd,
            acntgYear: acntgYear,
        };
    };

    // 응답 데이터를 공통 row 형식으로 정규화
    const normalizeRows = (list: any[]): RowData[] => {
        return (list || []).map((item: any) => {
            if (subTab === "DPST") {
                return {
                    date: item.dpstDt ?? "",
                    abstrCn: item.abstrCn ?? "",
                    amt: item.dpstAmt ?? 0,
                    rsdocNo: item.dpstRsdocNo ?? "",
                    docId:
                        item.ovrlMbsdsDpstDocId ||
                        item.mbsdsDpstDocId ||
                        item.spcAcntgDpstDocId ||
                        "",
                    rawData: item,
                };
            }

            return {
                date: item.implDt ?? "",
                abstrCn: item.abstrCn ?? "",
                amt: item.implAmt ?? 0,
                rsdocNo: item.expndRsdocNo ?? "",
                docId:
                    item.ovrlMbsdsExpndDocId ||
                    item.mbsdsExpndDocId ||
                    item.spcAcntgExpndDocId ||
                    "",
                rawData: item,
            };
        });
    };

    const calcSum = (rows: RowData[]) => {
        let total = 0;
        for (const row of rows) {
            total += Number(row.amt) || 0;
        }
        return Util.comma(String(total));
    };

    const selectList = () => {
        if (Util.isEmpty(ognzCd) || Util.isEmpty(acntgYear)) {
            setDataArr([]);
            setSumAmt("0");
            return;
        }

        AppContext.showSpinner();

        Axios.dataAccess({
            url: getApiUrl(),
            methodType: "post",
            paramType: "json",
            paramData: getParamData(),
            onSuccessFn: (res: any) => {
                const list = normalizeRows(res?.result?.list || []);
                setDataArr(list);
                setSumAmt(calcSum(list));
                AppContext.hideSpinner();
            }, 
        });
    };

    const onClickReset = () => {
        setUpOgnzCd(userInfo.upOgnzCd);
        setOgnzCd(userInfo.ognzCd);
        setKeyword("");
        setAcntgYear(Util.getNowAcntgYear());
    };

    // 탭/조건 변경 시 즉시 조회
    React.useEffect(() => {
        selectList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainTab, subTab, upOgnzCd, ognzCd, acntgYear]);

    return (
        <>
            <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
                {/* 타이틀 */}
                <div className="mt5">
                    <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">결재완료 결의서 첨부관리</span>
                    <div className="bdt3 bdc007bff pb10" />
                </div>

                {/* 1차 탭 */}
                <StyledTabs selectedIndex={mainTabIndex} className="tabFit">
                    <TabList>
                        <Tab
                            onClick={() => {
                                setMainTabIndex(0);
                                setMainTab("OVRL");
                            }}
                        >
                            총괄교부금
                        </Tab>
                        <Tab
                            onClick={() => {
                                setMainTabIndex(1);
                                setMainTab("MBSDS");
                            }}
                        >
                            운영비
                        </Tab>
                        <Tab
                            onClick={() => {
                                setMainTabIndex(2);
                                setMainTab("SPC");
                            }}
                        >
                            특별회계기금
                        </Tab>
                    </TabList>
                </StyledTabs>

                {/* 검색영역 */}
                <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                    <div className="dpFlx aiC flxWrap gap14">
                        <div className="dpFlx aiC">
                            <span className="w50 fm-c fs15 fc333333 ls1 taC">본부</span>
                            <SelectBox
                                value={upOgnzCd}
                                width={150}
                                height={36}
                                fontSize={14}
                                fontFamily="fm-c"
                                color="#333"
                                letterSpacing={-1}
                                borderColor="#ddd"
                                borderOnColor="#222"
                                borderRadius={2}
                                background="#FFF"
                                paddingRight={8}
                                codeArray={Util.getUpOrgCodeArray("01")}
                                placeholder="선택"
                                onChangeFunc={(data: string) => {
                                    setUpOgnzCd(data);
                                    setOgnzCd("");
                                }}
                            />
                        </div>

                        <div className="dpFlx aiC">
                            <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
                            <SelectBox
                                value={ognzCd}
                                width={180}
                                height={36}
                                fontSize={14}
                                fontFamily="fm-c"
                                color="#333"
                                letterSpacing={-1}
                                borderColor="#ddd"
                                borderOnColor="#222"
                                borderRadius={2}
                                background="#FFF"
                                paddingRight={8}
                                codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                                placeholder="선택"
                                onChangeFunc={(data: string) => setOgnzCd(data)}
                            />
                        </div>

                        <div className="dpFlx aiC">
                            <span className="w70 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                            <SelectBox
                                value={acntgYear}
                                width={120}
                                height={36}
                                fontSize={14}
                                fontFamily="fm-c"
                                color="#333"
                                letterSpacing={-1}
                                borderColor="#ddd"
                                borderOnColor="#222"
                                borderRadius={2}
                                background="#FFF"
                                paddingRight={8}
                                codeArray={Util.getAcntgYearArray()}
                                onChangeFunc={(v: string) => setAcntgYear(v)}
                            />
                        </div>

                        <div className="dpFlx aiC">
                            <span className="w70 fm-c fs15 fc333333 ls1 taL">문서번호</span>
                            <TextBox
                                value={keyword}
                                width={220}
                                height={36}
                                fontSize={14}
                                fontFamily="fm-c"
                                color="#333"
                                letterSpacing={-1}
                                borderColor="#ddd"
                                borderOnColor="#222"
                                borderRadius={2}
                                style={{ backgroundColor: "#fff", padding: "0 12px" }}
                                placeholder="문서번호 입력"
                                onChangeFunc={(t: string) => setKeyword(t)}
                            />
                        </div>

                        <div className="dpFlx aiC gap8 mlA">
                            <Button
                                buttonName="초기화"
                                width={100}
                                height={38}
                                fontFamily="fm-b"
                                fontSize={15}
                                letterSpacing={-1}
                                variant="styCancelBtn"
                                borderRadius={5}
                                buttonClass="dpFlx aiC jcC"
                                onClickFunc={onClickReset}
                            />
                        </div>
                    </div>
                </div>

                {/* 2차 탭 */}
                <StyledTabs selectedIndex={subTabIndex} className="tabFit">
                    <TabList>
                        <Tab
                            onClick={() => {
                                setSubTabIndex(0);
                                setSubTab("DPST");
                            }}
                        >
                            수입
                        </Tab>
                        <Tab
                            onClick={() => {
                                setSubTabIndex(1);
                                setSubTab("EXPND");
                            }}
                        >
                            지출
                        </Tab>
                    </TabList>
                </StyledTabs>

                {/* 리스트 */}
                <div className="bd1d9d9d9 bdrd6 bgcWhite p15">
                    <div className="dpFlx aiC jcSb mb10 h44">
                        <span className="fm-b fs16 fc007bff ls1 fw700">
                            {getTitle()} {getSubTitle()} 결의서 목록
                        </span>

                        <div className="dpFlx aiC">
                            <span className="fm-Pretendard-Light fs16 ls1 fc222222 pr3">합계</span>
                            <span className="fm-Pretendard-Medium fs16 ls1 fc007bff ml10">{sumAmt}</span>
                            <span className="fm-Pretendard-Light fs13 ls1 fc222222 pl2 pr3 mt3">원</span>
                        </div>
                    </div>

                    <GridTable
                        tableInfo={{
                            tableHeadVsb: true,
                            styleType: AdminGridStyle,
                            fldInfs: fldInfs,
                            ordFld: { name: "date", direction: "desc" },
                            dataArr: Util.filterFieldData(dataArr, fldInfs, "rsdocNo", keyword),
                            maxRowNums: [30, 50, 100],
                            theadTrHeight: 40,
                            tbodyTrHeight: 35,
                            paginateClass: "pt10",
                            paginateStyle: {
                                buttonOnColor: AppContext.boardThemeColor,
                                buttonWidth: 22,
                                buttonHeight: 24,
                                buttonMarginLeft: 1,
                                buttonMarginRight: 1,
                                fontSize: 13,
                                color: "#666666",
                                letterSpacing: -0.8,
                                fontFamily: "Pretendard-Regular",
                                borderRadius: 3,
                            },
                            footerVsb: true,
                            rowClickFunc: (item: any) => {
                                setSelectedDocId(item.docId);
                                setModalOpen(true);
                            },
                        }}
                    />
                </div>
            </div>

            <Modal
                modalOpen={modalOpen}
                titleEl={<DialogTitleBar title="첨부파일 관리" />}
                onCloseFunc={() => setModalOpen(false)}
            >
                <BgtCmptnAttachDetail
                    mainTab={mainTab}
                    subTab={subTab}
                    docId={selectedDocId}
                />
            </Modal>
        </>
    );
};

export default BgtCmptnAttachMng;